'use client';

import { type ProductBrief } from '@/domain/products/types';
import { type PlatformVersionDTO, type RoadMapUpdate } from '@/domain/roadmap/types';
import { useRoadmapEditStore } from '@/features/roadmap/store/roadmap';
import { useAuth } from '@/hooks/useAuth';
import { toDateString } from '@/utils/datetime';
import { isProductOwner, isTechnologyManager } from '@/utils/productPermission';
import { Typography } from '@andritzot/metris-web-ui/data-display/typography';
import { Autocomplete } from '@andritzot/metris-web-ui/inputs/autocomplete';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { type Control, Controller } from 'react-hook-form';

import { FieldActionsWrapper } from './shared/FieldActionsWrapper';

interface VersionFieldProps {
  viewOnly: boolean;
  isCustomerCare: boolean;
  control: Control<RoadMapUpdate, unknown>;
  product: ProductBrief | null;
  roadmapVersions: PlatformVersionDTO[];
  updateStatusesByVersion: (typeId: number, version: PlatformVersionDTO) => Promise<void>;
}

const versionLabel = (v: PlatformVersionDTO) => {
  if (!v) return '';
  const { name, releaseDate } = v;
  if (!releaseDate) return name;
  return `${name} (${toDateString(releaseDate)})`;
};

export function VersionField({
  viewOnly,
  isCustomerCare,
  control,
  product,
  roadmapVersions,
  updateStatusesByVersion,
}: Readonly<VersionFieldProps>) {
  const editableRoadMap = useRoadmapEditStore((s) => s.editableRoadMap);
  const { user, isAdmin } = useAuth();

  const canChangeVersion = isAdmin || isTechnologyManager(user, product) || isProductOwner(user, product);
  const label = isCustomerCare ? 'Fix Version' : 'Version';
  const isLocked = editableRoadMap.locked;
  const isDisabled = viewOnly || !canChangeVersion;

  const history =
    !isDisabled && editableRoadMap.versionChangesCount > 0
      ? {
          roadmapId: editableRoadMap.idRoadmap,
          field: 'IdRoadmapVersion',
          changesCount: editableRoadMap.versionChangesCount,
        }
      : undefined;

  return (
    <Controller
      name="version"
      control={control}
      render={({ field: { value, onChange } }) => (
        <Box sx={{ py: 0.75, width: '100%' }}>
          <FieldActionsWrapper locked={isLocked} history={history}>
            <Autocomplete<PlatformVersionDTO, false, false, false>
              value={value ?? null}
              onChange={async (_, newValue) => {
                onChange(newValue ?? null);
                if (newValue != null && editableRoadMap.type != null) {
                  await updateStatusesByVersion(editableRoadMap.type.idRoadmapType, newValue);
                }
              }}
              options={roadmapVersions}
              getOptionLabel={versionLabel}
              isOptionEqualToValue={(opt, v) => opt.idPlatformVersion === v.idPlatformVersion}
              disabled={isDisabled || product === null || roadmapVersions.length === 0}
              label={label}
              fullWidth
              size="small"
            />
          </FieldActionsWrapper>
          {!isDisabled && product == null && (
            <Typography variant="caption" color="warning.main">
              Select a product first
            </Typography>
          )}
        </Box>
      )}
    />
  );
}
